import {formatCompactNumber} from '@ark/react';
import {DateTime} from 'luxon';
import {ValueFormat} from './types';

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const countFormat = new Intl.NumberFormat('en-GB', {maximumFractionDigits: 1});
const percentFormat = new Intl.NumberFormat('en-GB', {
  style: 'percent',
  maximumFractionDigits: 1,
});
const axisPercentFormat = new Intl.NumberFormat('en-GB', {
  style: 'percent',
  maximumFractionDigits: 0,
});
const ratingFormat = new Intl.NumberFormat('en-GB', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const axisDecimalFormat = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 1,
});

export const formatDuration = (seconds: number) => {
  if (seconds < MINUTE - 0.5) return `${Math.round(seconds)}s`;
  if (seconds < HOUR - 30) return `${Math.round(seconds / MINUTE)}m`;
  if (seconds < DAY - 30 * MINUTE) {
    const minutes = Math.round(seconds / MINUTE);
    const remainder = minutes % 60;
    const hours = Math.floor(minutes / 60);
    if (remainder === 0) return `${hours}h`;
    return `${hours}h ${remainder}m`;
  }
  const hours = Math.round(seconds / HOUR);
  const remainder = hours % 24;
  const days = Math.floor(hours / 24);
  if (remainder === 0) return `${days}d`;
  return `${days}d ${remainder}h`;
};

const formatAxisDuration = (seconds: number) => {
  if (seconds === 0) return '0';
  if (seconds < MINUTE) return `${axisDecimalFormat.format(seconds)}s`;
  if (seconds < HOUR) return `${axisDecimalFormat.format(seconds / MINUTE)}m`;
  if (seconds < DAY) return `${axisDecimalFormat.format(seconds / HOUR)}h`;
  return `${axisDecimalFormat.format(seconds / DAY)}d`;
};

export const formatValue = (value: number | null, format: ValueFormat) => {
  if (value === null || Number.isNaN(value)) return '–';
  switch (format) {
    case 'duration':
      return formatDuration(value);
    case 'percent':
      return percentFormat.format(value);
    case 'rating':
      return ratingFormat.format(value);
    case 'count':
      return countFormat.format(value);
  }
};

export const formatAxisValue = (value: number, format: ValueFormat) => {
  switch (format) {
    case 'duration':
      return formatAxisDuration(value);
    case 'percent':
      return axisPercentFormat.format(value);
    case 'rating':
      return axisDecimalFormat.format(value);
    case 'count':
      return formatCompactNumber(value);
  }
};

export const formatDay = (isoDate: string) =>
  DateTime.fromISO(isoDate).toFormat('d MMM');

export const formatLongDay = (isoDate: string) =>
  DateTime.fromISO(isoDate).toFormat('ccc d MMM yyyy');

export const formatMonth = (isoDate: string) =>
  DateTime.fromISO(isoDate).toFormat('MMM yyyy');

export const formatLongMonth = (isoDate: string) =>
  DateTime.fromISO(isoDate).toFormat('MMMM yyyy');

export const formatDayRange = (minDays: number, maxDays: number | null) => {
  if (maxDays === null) return `${minDays}+ days`;
  if (minDays === maxDays) return minDays === 1 ? '1 day' : `${minDays} days`;
  return `${minDays}-${maxDays} days`;
};
